import React, { useMemo, useState, useEffect, useRef } from "react";
import { X, Search, MapPin, Users, Coins, Sparkles, Skull, Timer, Download, BookMarked, Loader2, RefreshCw } from "lucide-react";
import Button from "../../components/ui/Button";
import { LOCATION_CATEGORIES, EVENT_TYPES, cartomancerApi } from "./api";
import { CategoryIcon } from "./PinIcon";
import { formatDurationDays, formatDistance } from "./formatDuration";

function ModalShell({ title, subtitle, icon, onClose, children, testId }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6 bg-background/85 backdrop-blur-sm"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
      data-testid={testId}
    >
      <div className="rune-border bg-card rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="p-5 border-b border-border flex items-start justify-between">
          <div className="flex items-center gap-3">
            {icon}
            <div>
              <h3 className="font-display text-2xl text-gold">{title}</h3>
              {subtitle && <p className="font-serif-fantasy italic text-sm text-muted-foreground">{subtitle}</p>}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md border border-gold/30 text-muted-foreground hover:text-gold hover:border-gold hover:bg-gold/10 active:bg-gold/20 transition-all"
            data-testid={`${testId}-close`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-thin p-5">{children}</div>
      </div>
    </div>
  );
}

/* -------------------- TIMELINE -------------------- */

export function TimelinePanel({ logs, locations, map, onClose }) {
  // Order logs chronologically (assume they are stored chronologically by created_at desc; reverse)
  const items = [...(logs || [])].reverse();

  return (
    <ModalShell
      title="Timeline della Campagna"
      subtitle="La cronaca dei tuoi viaggi ed eventi"
      icon={<Timer className="w-6 h-6 text-arcane" />}
      onClose={onClose}
      testId="timeline-panel"
    >
      {items.length === 0 ? (
        <p className="text-center text-muted-foreground italic py-8">
          Nessun evento registrato. Conferma uno spostamento per iniziare la cronaca.
        </p>
      ) : (
        <ol className="relative border-l-2 border-gold/40 ml-4 space-y-6">
          {items.map((log) => (
            <li key={log.id} className="ml-6 relative" data-testid={`timeline-item-${log.id}`}>
              <span className="absolute -left-[34px] top-1 w-4 h-4 rounded-full bg-gold border-2 border-card" />
              <p className="font-heading text-[10px] tracking-widest text-gold uppercase">{log.date_str || "—"}</p>
              <h4 className="font-display text-lg text-parchment">{log.title}</h4>
              <p className="text-xs text-muted-foreground mt-1">
                {formatDistance(log.distance || 0, "")} · {formatDurationDays(log.duration_days || 0)}
              </p>
              {log.transports?.length > 0 && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  Mezzi: {log.transports.join(", ")}
                </p>
              )}
              {log.events?.length > 0 && (
                <ul className="mt-2 space-y-1 text-[11px] text-parchment">
                  {log.events.map((ev, i) => (
                    <li key={i}>· {EVENT_TYPES.find((t) => t.id === ev.type)?.label || ev.type}{ev.description ? ` — ${ev.description}` : ""}</li>
                  ))}
                </ul>
              )}
              {log.notes && <p className="mt-2 text-xs italic text-muted-foreground">« {log.notes} »</p>}
            </li>
          ))}
        </ol>
      )}
    </ModalShell>
  );
}

/* -------------------- JOURNAL (AI narrative) -------------------- */

export function JournalPanel({ logs, onClose }) {
  const [entries, setEntries] = useState(() => {
    const map = {};
    (logs || []).forEach((l) => {
      if (l.journal_text) map[l.id] = l.journal_text;
    });
    return map;
  });
  const [loadingIds, setLoadingIds] = useState(new Set());
  const [errorIds, setErrorIds] = useState({});
  const autoStartedRef = useRef(new Set());

  const generate = async (log) => {
    setLoadingIds((prev) => new Set(prev).add(log.id));
    setErrorIds((prev) => { const c = { ...prev }; delete c[log.id]; return c; });
    try {
      const res = await cartomancerApi.generateJournalEntry(log.id);
      setEntries((prev) => ({ ...prev, [log.id]: res.journal_text }));
    } catch (err) {
      setErrorIds((prev) => ({ ...prev, [log.id]: err.message || "Errore generazione" }));
    } finally {
      setLoadingIds((prev) => { const c = new Set(prev); c.delete(log.id); return c; });
    }
  };

  // Auto-generate on mount for logs that don't have a journal_text yet
  useEffect(() => {
    (logs || []).forEach((log) => {
      if (log.journal_text) return;
      if (entries[log.id]) return;
      if (autoStartedRef.current.has(log.id)) return;
      autoStartedRef.current.add(log.id);
      generate(log);
    });
    // eslint-disable-next-line
  }, [logs]);

  const updateText = (id, text) => setEntries((prev) => ({ ...prev, [id]: text }));

  const exportAll = () => {
    const chapters = (logs || []).slice().reverse().map((log, i) => {
      const text = entries[log.id] || "(Diario non ancora generato)";
      return `\n\n=== Capitolo ${i + 1}: ${log.title} ===\n${log.date_str || ""}\n\n${text}`;
    });
    const blob = new Blob([`Diario di Campagna\n${chapters.join("")}`], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "diario_campagna.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <ModalShell
      title="Diario Narrativo"
      subtitle="La saga della tua campagna, narrata da un cronista fantasy"
      icon={<BookMarked className="w-6 h-6 text-arcane" />}
      onClose={onClose}
      testId="journal-panel"
    >
      {(logs || []).length === 0 ? (
        <p className="text-center text-muted-foreground italic py-8">
          Il Diario è ancora vuoto. Ogni viaggio confermato aggiunge un capitolo.
        </p>
      ) : (
        <>
          <div className="flex justify-end mb-4">
            <Button variant="secondary" size="sm" onClick={exportAll} data-testid="journal-export-btn">
              <Download className="w-4 h-4 mr-2" /> Esporta TXT
            </Button>
          </div>
          <div className="space-y-6">
            {(logs || []).slice().reverse().map((log, i) => {
              const text = entries[log.id];
              const loading = loadingIds.has(log.id);
              const error = errorIds[log.id];
              return (
                <div key={log.id} className="rune-border rounded-md bg-card/50 p-4" data-testid={`journal-entry-${log.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <p className="font-heading text-[10px] tracking-widest text-gold uppercase">
                        Capitolo {i + 1} · {log.date_str || "—"}
                      </p>
                      <h4 className="font-display text-lg text-parchment">{log.title}</h4>
                    </div>
                    <button
                      onClick={() => generate(log)}
                      disabled={loading}
                      title="Rigenera diario"
                      className="p-1.5 rounded border border-arcane/40 hover:border-arcane hover:bg-arcane/10 text-arcane transition-all disabled:opacity-40"
                      data-testid={`journal-regenerate-${log.id}`}
                    >
                      {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                  {error && (
                    <p className="text-xs text-ruby mb-2" data-testid={`journal-error-${log.id}`}>« {error} »</p>
                  )}
                  {loading && !text ? (
                    <div className="flex items-center gap-2 text-arcane font-serif-fantasy italic text-sm py-3">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Il cronista sta redigendo il capitolo...
                    </div>
                  ) : text ? (
                    <textarea
                      value={text}
                      onChange={(e) => updateText(log.id, e.target.value)}
                      rows={Math.max(6, Math.min(20, text.split("\n").length + 2))}
                      className="w-full bg-input border border-gold/20 rounded px-3 py-2 text-sm font-serif-fantasy leading-relaxed"
                      data-testid={`journal-textarea-${log.id}`}
                    />
                  ) : (
                    <p className="text-xs italic text-muted-foreground">
                      Preparazione del capitolo in corso...
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </ModalShell>
  );
}

/* -------------------- WIKI (encyclopedia) -------------------- */

export function WikiPanel({ locations, logs, onClose }) {
  const [tab, setTab] = useState("places");
  const [q, setQ] = useState("");

  const filteredPlaces = useMemo(() => {
    return (locations || []).filter((l) => l.name.toLowerCase().includes(q.toLowerCase()));
  }, [locations, q]);

  // NPCs: aggregate from every location's `npcs` text (split by newline or comma)
  const npcs = useMemo(() => {
    const items = [];
    (locations || []).forEach((l) => {
      (l.npcs || "").split(/\n|,/).map((s) => s.trim()).filter(Boolean).forEach((name) => {
        items.push({ name, location: l.name, locationId: l.id });
      });
    });
    return items.filter((n) => n.name.toLowerCase().includes(q.toLowerCase()));
  }, [locations, q]);

  const treasures = useMemo(() => {
    const items = [];
    (locations || []).forEach((l) => {
      (l.treasures || "").split(/\n|,/).map((s) => s.trim()).filter(Boolean).forEach((name) => {
        items.push({ name, location: l.name });
      });
    });
    return items.filter((t) => t.name.toLowerCase().includes(q.toLowerCase()));
  }, [locations, q]);

  // Events (from logs)
  const events = useMemo(() => {
    const items = [];
    (logs || []).forEach((log) => {
      (log.events || []).forEach((ev) => {
        if (ev.description) items.push({ description: ev.description, date: log.date_str, log: log.title });
      });
    });
    return items.filter((e) => (e.description || "").toLowerCase().includes(q.toLowerCase()));
  }, [logs, q]);

  const tabs = [
    { id: "places", label: "Luoghi", icon: <MapPin className="w-4 h-4" />, count: filteredPlaces.length },
    { id: "npcs", label: "NPC", icon: <Users className="w-4 h-4" />, count: npcs.length },
    { id: "items", label: "Oggetti", icon: <Coins className="w-4 h-4" />, count: treasures.length },
    { id: "events", label: "Eventi", icon: <Skull className="w-4 h-4" />, count: events.length },
    { id: "factions", label: "Fazioni", icon: <Sparkles className="w-4 h-4" />, count: 0 },
  ];

  return (
    <ModalShell
      title="Enciclopedia della Campagna"
      subtitle="La wiki personale del tuo mondo"
      icon={<Sparkles className="w-6 h-6 text-arcane" />}
      onClose={onClose}
      testId="wiki-panel"
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2 rune-border rounded-md bg-card/50 px-3 py-2">
          <Search className="w-4 h-4 text-gold" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca luoghi, NPC, oggetti..."
            className="flex-1 bg-transparent focus:outline-none text-sm"
            data-testid="wiki-search-input"
          />
        </div>

        <div className="flex flex-wrap gap-1 border-b border-gold/20 pb-2">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded text-[10px] font-heading tracking-widest uppercase transition-all ${
                tab === t.id ? "bg-gold/20 text-gold border border-gold" : "border border-transparent text-muted-foreground hover:text-gold"
              }`}
              data-testid={`wiki-tab-${t.id}`}
            >
              {t.icon} {t.label} ({t.count})
            </button>
          ))}
        </div>

        <div className="min-h-[240px]">
          {tab === "places" && (
            filteredPlaces.length === 0 ? (
              <p className="text-xs italic text-muted-foreground text-center py-8">Nessun luogo trovato.</p>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2">
                {filteredPlaces.map((l) => (
                  <div key={l.id} className="rune-border rounded p-3 bg-card/40" data-testid={`wiki-place-${l.id}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <CategoryIcon id={l.category} className="w-4 h-4" />
                      <p className="font-heading text-sm text-gold truncate">{l.name}</p>
                    </div>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                      {LOCATION_CATEGORIES.find((c) => c.id === l.category)?.label}
                    </p>
                    {l.description && <p className="text-xs text-parchment line-clamp-3">{l.description}</p>}
                  </div>
                ))}
              </div>
            )
          )}
          {tab === "npcs" && (
            npcs.length === 0 ? (
              <p className="text-xs italic text-muted-foreground text-center py-8">Nessun NPC. Aggiungi NPC ai tuoi luoghi.</p>
            ) : (
              <div className="grid gap-1 sm:grid-cols-2">
                {npcs.map((n, i) => (
                  <div key={i} className="rune-border rounded p-2 bg-card/40 flex items-center gap-2" data-testid={`wiki-npc-${i}`}>
                    <Users className="w-4 h-4 text-arcane" />
                    <div className="min-w-0">
                      <p className="text-xs text-parchment truncate">{n.name}</p>
                      <p className="text-[10px] text-muted-foreground truncate">In: {n.location}</p>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
          {tab === "items" && (
            treasures.length === 0 ? (
              <p className="text-xs italic text-muted-foreground text-center py-8">Nessun oggetto/tesoro registrato.</p>
            ) : (
              <div className="grid gap-1 sm:grid-cols-2">
                {treasures.map((t, i) => (
                  <div key={i} className="rune-border rounded p-2 bg-card/40 flex items-center gap-2">
                    <Coins className="w-4 h-4 text-gold" />
                    <div className="min-w-0">
                      <p className="text-xs text-parchment truncate">{t.name}</p>
                      <p className="text-[10px] text-muted-foreground truncate">Da: {t.location}</p>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
          {tab === "events" && (
            events.length === 0 ? (
              <p className="text-xs italic text-muted-foreground text-center py-8">Nessun evento narrato ancora.</p>
            ) : (
              <ul className="space-y-2">
                {events.map((e, i) => (
                  <li key={i} className="rune-border rounded p-3 bg-card/40 text-xs">
                    <p className="font-heading text-[10px] tracking-widest text-gold uppercase">{e.date} · {e.log}</p>
                    <p className="text-parchment mt-1">« {e.description} »</p>
                  </li>
                ))}
              </ul>
            )
          )}
          {tab === "factions" && (
            <p className="text-xs italic text-muted-foreground text-center py-8">
              Le fazioni saranno accessibili nelle prossime versioni. Aggiungile alle Note del DM di ogni luogo per ora.
            </p>
          )}
        </div>
      </div>
    </ModalShell>
  );
}
